<template>
  <div
    :id="ids.wrapper"
    ref="wrapperRef"
    v-click-outside="event => handleShow(event, false)"
    :data-testid="props.dataTestid"
    class="popover-wrapper inline relative"
    :class="{
      'cursor-pointer': props.actionType !== 'disable'
    }"
  >
    <div
      :id="ids.trigger"
      ref="mainRef"
      class="flex"
      @click.stop="event => handleShow(event)"
      @mouseover="event => handleShow(event, true)"
      @mouseout="event => handleShow(event, false)"
    >
      <slot>
        <V2IconsInfoCircle
          ref="tooltipIconRef"
          type="qs-mark-outline"
          :width="16"
          :height="16"
          :fill="semanticColorLookup('primary.default')"
        />
      </slot>
    </div>

    <ClientOnly>
      <Teleport to="body">
        <V2AtomsTooltip
          v-if="theme === 'default'"
          :id="ids.tooltip"
          v-model="show"
          class="fixed top-0 max-w-[250px] xs:max-w-[328px] w-max z-[99999]"
          :type="type"
          :has-icon="hasIcon"
          :size="size"
          :tip-x-adjustment="arrowOffsetPx"
          :append-popover-class="popoverClass"
          @mouseover="event => handleShow(event, true)"
          @mouseout="event => handleShow(event, false)"
        >
          <template #title>
            <slot
              v-if="$slots.title"
              name="title"
            >
            </slot>
          </template>

          <template #description>
            <slot
              v-if="$slots?.description"
              name="description"
            >
            </slot>
          </template>
        </V2AtomsTooltip>

        <template v-else-if="theme === 'card'">
          <div
            v-show="show"
            :id="ids.tooltip"
            class="fixed top-0 max-w-[250px] xs:max-w-[328px] md:max-w-[420px] w-max z-[99999]"
            :class="popoverClass"
            @mouseover="event => handleShow(event, true)"
            @mouseout="event => handleShow(event, false)"
          >
            <div class="relative bg-semantic-surface-default shadow-combined-elevation-3 rounded-3xl p-4 md:p-6 md:pt-8">
              <V2IconsClose
                class="wh-6 absolute top-2 right-2 md:top-4 md:right-4 cursor-pointer z-[3]"
                type="default-bold"
                :fill="semanticColorLookup('surface.on-surface-variant')"
                @click="show = false"
              />

              <slot name="content" />
            </div>
          </div>
        </template>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<script setup>
import { useId } from 'vue'

defineOptions({
  name: 'V2MoleculesPopover'
})

const props = defineProps({
  type: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error'].includes(value)
  },

  actionType: {
    type: String,
    default: 'hover',
    validator: value => ['click', 'hover', 'disable'].includes(value)
  },

  hasIcon: {
    type: Boolean,
    default: false
  },

  size: {
    type: String,
    default: 'large',
    validator: value => ['base', 'large'].includes(value)
  },

  popoverClass: {
    type: String,
    default: ''
  },

  groupKey: {
    type: String,
    default: ''
  },

  dataTestid: {
    type: String,
    default: ''
  },

  theme: {
    type: String,
    default: 'default',
    validator: value => ['default', 'card'].includes(value)
  },

  // Initial vertical position only — overwritten once the tooltip doesn't fit within the viewport
  initPosY: {
    type: String,
    default: 'top',
    validator: value => ['top', 'bottom'].includes(value)
  },

  // Initial horizontal position only — overwritten once the tooltip doesn't fit within the viewport
  initPosX: {
    type: String,
    default: 'center',
    validator: value => ['left', 'center', 'right'].includes(value)
  },

  // Gap between the tooltip and the trigger when positioned above it
  topOffset: {
    type: Number,
    default: -5
  },

  // Gap between the tooltip and the trigger when positioned below it
  bottomOffset: {
    type: Number,
    default: 12
  }
})

const UID = useId()

const show = ref(false)

const mainRef = ref(null)
const wrapperRef = ref(null)

// Drives V2AtomsTooltip's own `tipXAdjustment` prop (its supported extension point
// for overriding the arrow's horizontal position) instead of writing `arrow.style.left`
// imperatively — the atom's internal watcher on that same prop already owns `left`/
// `transform` on the arrow, so writing to it directly from here races with that watcher
// and can get silently reverted after every `show` toggle.
const arrowOffsetPx = ref('0px')

const ids = computed(() => ({
  wrapper: `popover-${UID}`,
  trigger: `popover-trigger-${UID}`,
  tooltip: `popover-tooltip-${UID}`
}))

// Global event used to coordinate popovers in the same group.
// When one popover opens in click mode, siblings with the same groupKey close.
const POPOVER_OPEN_EVENT = 'cc-popover-open'

const els = computed(() => {
  const tooltip = import.meta.client && document.querySelector(`#${ids.value.tooltip}`)
  const arrow = tooltip && tooltip.querySelector('.tooltip-arrow')

  return {
    wrapper: wrapperRef.value,
    main: mainRef.value,
    tooltip,
    arrow
  }
})

// Viewport gutter the tooltip box must never cross, and the minimum margin
// kept between the arrow and the tooltip's own edges, so it never renders
// detached from the box (even once the box itself has been clamped).
const GUTTER = 8
const ARROW_MARGIN = 8

let rafId = null

function scheduleRecalculate () {
  if (rafId) {
    return
  }

  rafId = requestAnimationFrame(() => {
    rafId = null
    calculatePosition()
  })
}

useEventListener(window, 'resize', scheduleRecalculate)
useEventListener(window, 'scroll', scheduleRecalculate)

// Slotted title/description content can reflow after first paint (e.g.
// dynamic option copy) — re-measure whenever the tooltip box itself resizes.
let tooltipResizeObserver = null
let observedTooltip = null

// Since the tooltip is teleported to <body>, it's not a DOM descendant of the
// trigger — moving the pointer from one to the other fires a mouseout/mouseover
// pair rather than staying within the same subtree, so we check relatedTarget
// against both elements to avoid flicker/closing while hovering across them.
function isWithinPopover (target) {
  if (!target) {
    return false
  }

  const { main, tooltip } = els.value

  return Boolean((main && main.contains(target)) || (tooltip && tooltip.contains(target)))
}

async function handleShow ($event, _show = null) {
  const eventType = $event?.type

  const actionMap = getKey(eventType, {
    click: 'click',
    mouseover: 'hover',
    mouseout: 'hover'
  })

  if (props.actionType !== actionMap) {
    return
  }

  if (eventType === 'mouseout' && isWithinPopover($event.relatedTarget)) {
    return
  }

  if (_show === show.value) {
    return
  }

  if (_show === null || _show === '') {
    show.value = !show.value
  } else {
    show.value = _show
  }

  // Broadcast currently opened popover so other instances in the same group
  // can close themselves (accordion-style behavior for popovers).
  if (show.value && props.actionType === 'click' && props.groupKey && import.meta.client) {
    window.dispatchEvent(new CustomEvent(POPOVER_OPEN_EVENT, {
      detail: {
        groupKey: props.groupKey,
        id: ids.value.wrapper
      }
    }))
  }

  await calculatePosition()
}

function handlePopoverGroupOpen (event) {
  if (!props.groupKey || props.actionType !== 'click') {
    return
  }

  const detail = event?.detail || {}
  const isSameGroup = detail.groupKey === props.groupKey
  const isDifferentPopover = detail.id !== ids.value.wrapper

  // Close only when another popover from the same group was opened.
  if (isSameGroup && isDifferentPopover) {
    show.value = false
  }
}

onMounted(() => {
  if (import.meta.client) {
    window.addEventListener(POPOVER_OPEN_EVENT, handlePopoverGroupOpen)
    tooltipResizeObserver = new ResizeObserver(scheduleRecalculate)
  }
})

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener(POPOVER_OPEN_EVENT, handlePopoverGroupOpen)
    tooltipResizeObserver?.disconnect()
    observedTooltip = null
  }

  if (rafId) {
    cancelAnimationFrame(rafId)
  }
})

async function calculatePosition () {
  if (!show.value) {
    return
  }

  await nextTick()

  const WINDOW_WIDTH = window.innerWidth || document.documentElement.clientWidth
  const WINDOW_HEIGHT = window.innerHeight || document.documentElement.clientHeight

  const wrapper = els.value.wrapper
  const main = els.value.main
  const tooltip = els.value.tooltip

  const arrow = els.value.arrow
  const arrowSvg = (arrow && arrow.querySelector('svg')) || null

  if (!wrapper || !main || !tooltip) {
    return
  }

  // Only (re-)observe when the tooltip node itself changes. Observing fires an
  // initial callback as soon as an element starts being watched (per spec), and
  // that callback is `scheduleRecalculate` — re-observing on every call here would
  // re-trigger that initial callback every time, looping calculatePosition() on a
  // rAF for as long as the tooltip stays open.
  if (tooltipResizeObserver && tooltip !== observedTooltip) {
    tooltipResizeObserver.disconnect()
    tooltipResizeObserver.observe(tooltip)
    observedTooltip = tooltip
  }

  // The tooltip is `position: fixed`, so all coordinates below are viewport
  // (not document/page) relative — no scroll-offset conversion needed.
  const wrapperRect = wrapper.getBoundingClientRect()
  const mainRect = main.getBoundingClientRect()
  const tooltipRect = tooltip.getBoundingClientRect()
  const arrowRect = arrow && arrow.getBoundingClientRect()

  const viewport = {
    top: (wrapperRect.top - tooltipRect.height) >= 0,
    bottom: (wrapperRect.bottom + tooltipRect.height) <= WINDOW_HEIGHT
  }

  const preferTop = props.initPosY === 'top'

  let isTop = preferTop

  if (preferTop && !viewport.top) {
    isTop = false
  } else if (!preferTop && !viewport.bottom) {
    isTop = true
  }

  // Candidate X, before clamping to the viewport
  const candidateX = (() => {
    if (props.initPosX === 'left') {
      return wrapperRect.left
    }

    if (props.initPosX === 'right') {
      return wrapperRect.left + mainRect.width - tooltipRect.width
    }

    return wrapperRect.left - ((tooltipRect.width - mainRect.width) / 2)
  })()

  // Clamp so the box always stays fully on-screen. `maxX` can dip below
  // `GUTTER` when the tooltip is wider than the viewport (small phones) —
  // in that case fall back to `GUTTER` on both sides rather than overflow.
  const maxX = Math.max(WINDOW_WIDTH - tooltipRect.width - GUTTER, GUTTER)
  const x = Math.min(Math.max(candidateX, GUTTER), maxX)

  const y = isTop
    ? Math.max((wrapperRect.top - tooltipRect.height) + props.topOffset, GUTTER)
    : Math.min(wrapperRect.bottom + props.bottomOffset, WINDOW_HEIGHT - tooltipRect.height - GUTTER)

  tooltip.style.left = `${x}px`
  tooltip.style.top = `${y}px`

  // Vertical side of the arrow (top/bottom edge of the box) and its rotation are
  // still set directly here — `V2AtomsTooltip` has no watcher on these, so there's
  // no writer to race with.
  if (arrow) {
    if (arrowSvg) {
      arrowSvg.style.transform = isTop
        ? 'rotate(0deg)'
        : 'rotate(180deg)'
    }

    arrow.style.position = 'absolute'
    arrow.style.top = isTop
      ? 'auto'
      : arrowRect
        ? `-${Math.floor(arrowRect.height)}px`
        : 'auto'
    arrow.style.bottom = isTop
      ? '-0.4375rem'
      : 'auto'
  }

  // Point the arrow at the center of the trigger, clamped so it always stays within
  // the (possibly clamped) tooltip box and never renders detached. Applied through
  // V2AtomsTooltip's `tipXAdjustment` prop (see the ref's comment) rather than by
  // writing `arrow.style.left` here directly.
  const arrowWidth = arrowRect?.width || 0
  const triggerCenter = mainRect.left + (mainRect.width / 2)
  const rawArrowLeft = (triggerCenter - x) - (arrowWidth / 2)
  const maxArrowLeft = Math.max(tooltipRect.width - ARROW_MARGIN - arrowWidth, ARROW_MARGIN)

  arrowOffsetPx.value = `${Math.min(Math.max(rawArrowLeft, ARROW_MARGIN), maxArrowLeft)}px`
}
</script>
